/**
 * El respaldo CON fotos: un ZIP que envuelve el respaldo de siempre
 * (design.md 10.7; Req. 6.17).
 *
 *   respaldo.json      ← byte por byte el mismo archivo que el respaldo sin fotos
 *   fotos/<id>.jpg     ← una entrada por foto
 *
 * Por qué así y no con las fotos dentro del JSON: en base64 cada foto crece
 * un 33 % y el archivo se vuelve impracticable con el tiempo; y, sobre todo,
 * el JSON seguiría siendo el mismo formato de siempre solo de nombre. Aquí el
 * JSON no cambia nada: el respaldo sin fotos y el ZIP traen exactamente el
 * mismo texto, así que una versión vieja de la app podría abrir el JSON de
 * adentro y restaurar todo menos las fotos.
 *
 * Cada foto viaja con su SHA-256 dentro del JSON, y el JSON va cubierto por
 * la suma de verificación del respaldo. Así la suma protege las fotos de
 * forma transitiva: al restaurar se vuelve a calcular la huella de cada
 * archivo del ZIP y una foto dañada no entra, se reporta y no se borra nada.
 *
 * Código puro: no toca IndexedDB.
 */

import { unzipSync, zipSync, strFromU8, strToU8 } from 'fflate';
import { ErrorRespaldo } from '@/domain/errors';
import { calcularChecksum, sha256DeBytes } from './checksum';
import {
  NOMBRE_DEL_JSON,
  idDesdeRuta,
  revisarFotosDelRespaldo,
  rutaDeFoto,
  textoDeProblemas,
  type ProblemaDeFoto,
} from '@/domain/fotos';
import {
  analizarRespaldo,
  calcularRangoFechas,
  nombreArchivoRespaldo,
  verificarRespaldoGenerado,
  type ArchivoRespaldo,
  type RespaldoAnalizado,
} from './backup-format';
import {
  cifrarBytes,
  contrasenaCorrecta,
  descifrarBytes,
  leerEnvolturaCifrada,
  serializarEnvoltura,
} from './backup-crypto';
import type { Bytes, CifradoRespaldo, Photo } from './types';

/** Los primeros bytes de todo ZIP: "PK\x03\x04". */
const FIRMA_ZIP = [0x50, 0x4b, 0x03, 0x04] as const;

/**
 * ¿Estos bytes son un ZIP? Es lo que distingue un respaldo con fotos de uno
 * en JSON después de descifrarlo, sin necesidad de un campo nuevo en la
 * envoltura que dejaría sin abrir los archivos cifrados anteriores.
 */
export function esZip(bytes: Bytes): boolean {
  return FIRMA_ZIP.every((b, i) => bytes[i] === b);
}

// ---------------------------------------------------------------------------
// Armar y leer el ZIP
// ---------------------------------------------------------------------------

/**
 * Arma el ZIP. El JSON se comprime (es texto y baja mucho); las fotos no, que
 * un JPEG ya está comprimido y apretarlo otra vez solo gastaría segundos de
 * iPhone sin ahorrar espacio.
 */
/**
 * La fecha que llevan las entradas del ZIP: la del respaldo, no la del
 * momento de armarlo, así que dos respaldos de los mismos datos dan el mismo
 * archivo en el mismo teléfono.
 *
 * El formato ZIP guarda la fecha en hora local y solo llega de 1980 a 2099.
 * Una fecha fuera de ahí (o un archivo con la fecha alterada) usa 1980 en
 * lugar de hacer fallar el respaldo por un dato que no importa.
 */
function fechaDeLasEntradas(exportedAt?: string): Date {
  const instante = exportedAt === undefined ? Number.NaN : Date.parse(exportedAt);
  if (!Number.isNaN(instante)) {
    const fecha = new Date(instante);
    const ano = fecha.getFullYear();
    if (ano >= 1980 && ano <= 2099) return fecha;
  }
  return new Date(1980, 0, 1);
}

export function armarZip(
  json: string,
  fotos: ReadonlyMap<string, Bytes>,
  exportedAt?: string,
): Bytes {
  const mtime = fechaDeLasEntradas(exportedAt);
  const contenido: Record<string, [Bytes, { level: 0 | 9; mtime: Date }]> = {
    [NOMBRE_DEL_JSON]: [strToU8(json), { level: 9, mtime }],
  };
  for (const [id, bytes] of fotos) {
    contenido[rutaDeFoto(id)] = [bytes, { level: 0, mtime }];
  }
  return zipSync(contenido);
}

export interface ContenidoDelZip {
  /** El texto de `respaldo.json`, tal cual venía. */
  json: string;
  /** Los bytes de cada foto, por identificador. */
  fotos: Map<string, Bytes>;
  /** Lo que no es el JSON ni una foto: se omite y se dice. */
  omitidos: string[];
}

/** Abre el ZIP. Lanza si no se puede leer o si no trae el respaldo. */
export function leerZip(bytes: Bytes): ContenidoDelZip {
  let entradas: Record<string, Bytes>;
  try {
    // fflate declara sus arreglos sobre cualquier búfer; los que entrega
    // siempre viven en un ArrayBuffer normal (ver `Bytes` en types.ts).
    entradas = unzipSync(bytes) as Record<string, Bytes>;
  } catch {
    throw new ErrorRespaldo(
      'El archivo no se pudo abrir: está dañado o no es un respaldo con fotos de DuoFit. ' +
        'No se modificó ningún dato.',
    );
  }

  const json = entradas[NOMBRE_DEL_JSON];
  if (json === undefined) {
    throw new ErrorRespaldo(
      `Ese archivo no trae "${NOMBRE_DEL_JSON}", así que no es un respaldo de DuoFit. ` +
        'No se modificó ningún dato.',
    );
  }

  const fotos = new Map<string, Bytes>();
  const omitidos: string[] = [];
  for (const [ruta, datos] of Object.entries(entradas)) {
    if (ruta === NOMBRE_DEL_JSON) continue;
    // Solo `fotos/<id>.jpg`. El nombre no puede salirse de su carpeta: lo que
    // no cuadre con eso se omite en lugar de escribirse donde sea.
    const id = idDesdeRuta(ruta);
    if (id === null) omitidos.push(ruta);
    else fotos.set(id, datos);
  }

  let texto: string;
  try {
    texto = strFromU8(json);
  } catch {
    throw new ErrorRespaldo(
      'El respaldo que viene dentro del archivo no se pudo leer. No se modificó ningún dato.',
    );
  }
  return { json: texto, fotos, omitidos };
}

/** La huella de cada foto del ZIP, para comparar con la que declara el JSON. */
async function huellasDe(fotos: ReadonlyMap<string, Bytes>): Promise<Map<string, string>> {
  const huellas = new Map<string, string>();
  for (const [id, bytes] of fotos) huellas.set(id, await sha256DeBytes(bytes));
  return huellas;
}

/**
 * Cuáles fotos entran al respaldo y cuáles no, comparando los bytes que hay
 * con la huella que se guardó al tomarlas. Una foto que se dañó en el
 * almacenamiento NO se mete al archivo: se queda en el teléfono y se avisa,
 * en lugar de respaldar una imagen rota con cara de buena.
 */
export async function fotosQueEntran(
  declaradas: readonly Pick<Photo, 'id' | 'sha256'>[],
  bytes: ReadonlyMap<string, Bytes>,
): Promise<{ buenas: Map<string, Bytes>; problemas: ProblemaDeFoto[] }> {
  const revision = revisarFotosDelRespaldo(declaradas, await huellasDe(bytes));
  const entran = new Set(revision.buenas);
  return {
    buenas: new Map([...bytes].filter(([id]) => entran.has(id))),
    problemas: revision.problemas,
  };
}

/**
 * El mismo archivo, declarando estas fotos: la lista, su conteo, el rango de
 * fechas y la suma de verificación al día. La suma se vuelve a calcular
 * porque cubre `data`, y es la que después protege la huella de cada foto.
 */
export async function conFotosDeclaradas(
  archivo: ArchivoRespaldo,
  photos: readonly Photo[],
): Promise<ArchivoRespaldo> {
  const data = { ...archivo.data, photos: [...photos] };
  return {
    ...archivo,
    counts: { ...archivo.counts, photos: data.photos.length },
    dateRange: calcularRangoFechas(data),
    checksum: await calcularChecksum(data),
    data,
  };
}

// ---------------------------------------------------------------------------
// Restaurar desde el ZIP
// ---------------------------------------------------------------------------

export interface RespaldoConFotosAnalizado {
  /** El respaldo de dentro, ya analizado; sus `photos` son las que sí entran. */
  analizado: RespaldoAnalizado;
  /** Los bytes de cada foto que pasó la verificación. */
  fotos: Map<string, Bytes>;
  /** Las fotos que no entran, con su motivo. */
  problemas: ProblemaDeFoto[];
}

/**
 * Lee un ZIP y lo analiza como cualquier respaldo, verificando además cada
 * foto contra su SHA-256. No toca la base.
 *
 * Una foto que falta, que llegó dañada o que el JSON no menciona NO entra, y
 * su registro se quita de `photos` para que no quede un dato prometiendo una
 * foto que no está. Todo lo demás se restaura igual: una foto dañada no deja
 * sin restaurar el resto.
 */
export async function analizarRespaldoConFotos(
  bytes: Bytes,
): Promise<RespaldoConFotosAnalizado> {
  const { json, fotos, omitidos } = leerZip(bytes);
  const analizado = await analizarRespaldo(json);

  const declaradas = analizado.datos.photos;
  const revision = revisarFotosDelRespaldo(declaradas, await huellasDe(fotos));
  const buenas = new Set(revision.buenas);

  const aviso = textoDeProblemas(revision.problemas);
  const advertencias = [...analizado.resumen.advertencias];
  if (aviso !== null) advertencias.push(`De las fotos de progreso: ${aviso}.`);
  if (omitidos.length > 0) {
    advertencias.push(
      `El archivo trae contenido que no se reconoce y se omitirá: ${omitidos.join(', ')}.`,
    );
  }

  return {
    analizado: {
      ...analizado,
      datos: { ...analizado.datos, photos: declaradas.filter((f) => buenas.has(f.id)) },
      resumen: { ...analizado.resumen, advertencias },
    },
    fotos: new Map([...fotos].filter(([id]) => buenas.has(id))),
    problemas: revision.problemas,
  };
}

// ---------------------------------------------------------------------------
// Verificar el ZIP generado, antes de entregarlo (Req. 6.13)
// ---------------------------------------------------------------------------

/**
 * Vuelve a abrir el ZIP recién armado como si fuera un archivo elegido para
 * restaurar y exige que todo cuadre: el JSON idéntico, la suma válida, los
 * conteos de la base y la huella de cada foto. Lanza si algo falla: no se
 * entrega nada.
 */
export async function verificarZipGenerado(
  bytes: Bytes,
  esperado: { texto: string; archivo: ArchivoRespaldo },
): Promise<void> {
  const fallo = (detalle: string) =>
    new ErrorRespaldo(
      `No se pudo verificar el respaldo con fotos (${detalle}). No se entregó ningún ` +
        'archivo; vuelve a intentarlo.',
    );

  let contenido: ContenidoDelZip;
  try {
    contenido = leerZip(bytes);
  } catch (error) {
    throw fallo(error instanceof Error ? error.message : 'no se pudo abrir');
  }
  if (contenido.json !== esperado.texto) throw fallo('el respaldo de dentro no es el mismo');
  if (contenido.omitidos.length > 0) throw fallo(`sobra ${contenido.omitidos.join(', ')}`);

  await verificarRespaldoGenerado(contenido.json, esperado.archivo.counts);

  // Cada foto declarada está, con su huella, y no sobra ninguna
  const revision = revisarFotosDelRespaldo(
    esperado.archivo.data.photos,
    await huellasDe(contenido.fotos),
  );
  const aviso = textoDeProblemas(revision.problemas);
  if (aviso !== null) throw fallo(aviso);
  // Y el conteo de la cabecera cuadra con lo que de verdad trae
  const declaradas = esperado.archivo.counts['photos'] ?? 0;
  if (revision.buenas.length !== declaradas) {
    throw fallo(`${revision.buenas.length} fotos en el archivo y ${declaradas} en la cabecera`);
  }
}

// ---------------------------------------------------------------------------
// Cifrar el respaldo con fotos (Req. 6.21)
// ---------------------------------------------------------------------------

export interface ZipCifrado {
  texto: string;
  nombre: string;
}

/**
 * Cifra el ZIP completo con la contraseña configurada y comprueba el archivo
 * resultante antes de entregarlo: lo descifra, abre el ZIP y verifica el
 * respaldo y cada foto.
 */
export async function cifrarZip(
  zip: Bytes,
  esperado: { texto: string; archivo: ArchivoRespaldo },
  contrasena: string,
  configurado: CifradoRespaldo,
  opciones: { fecha?: Date; iteraciones?: number } = {},
): Promise<ZipCifrado> {
  if (!(await contrasenaCorrecta(contrasena, configurado))) {
    throw new ErrorRespaldo(
      'La contraseña no es la que configuraste para los respaldos. Revisa cómo la escribiste.',
    );
  }

  const envoltura = await cifrarBytes(zip, contrasena, {
    exportedAt: esperado.archivo.exportedAt,
    ...(opciones.iteraciones !== undefined ? { iteraciones: opciones.iteraciones } : {}),
  });
  const texto = serializarEnvoltura(envoltura);

  // Prueba de descifrado: lo que se entrega se puede abrir y está completo.
  const leida = leerEnvolturaCifrada(texto);
  if (leida === null) throw new ErrorRespaldo('No se pudo verificar el respaldo cifrado.');
  const descifrado = await descifrarBytes(leida, contrasena);
  if (!esZip(descifrado)) {
    throw new ErrorRespaldo('El respaldo cifrado no quedó con el archivo adentro. No se entregó.');
  }
  await verificarZipGenerado(descifrado, esperado);

  return { texto, nombre: nombreArchivoRespaldo(opciones.fecha ?? new Date(), true, true) };
}
